import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";

import {
  fetchFirmwareDetail,
  fetchLatestFirmwareCheckRun,
  firmwareCheckRunKey,
  firmwareDetailKey,
  runFirmwareCommandSequence,
  startFirmwareCheckRun,
  type FirmwareCheckDocument,
  type FirmwareCheckSection,
} from "./firmwareApi";

type Row = Record<string, string | number | boolean | null>;

function sectionRows(document: FirmwareCheckDocument | null | undefined, key: string): Row[] {
  const section = document?.sections?.find((item: FirmwareCheckSection) => item.key === key);
  return section?.rows ?? [];
}

function text(value: Row[string]): string {
  if (value == null) return "";
  return String(value);
}

export function FirmwareLogicAnalyzerPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const firmwareId = Number.parseInt(params.id ?? "", 10);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [sequenceText, setSequenceText] = useState("");
  const [steps, setSteps] = useState<Row[]>([]);
  const [sequenceDocument, setSequenceDocument] = useState<FirmwareCheckDocument | null>(null);

  const detailQuery = useQuery({
    queryKey: firmwareDetailKey(firmwareId),
    queryFn: () => fetchFirmwareDetail(firmwareId),
    enabled: Number.isFinite(firmwareId) && firmwareId > 0,
  });
  const checkQuery = useQuery({
    queryKey: firmwareCheckRunKey(firmwareId),
    queryFn: () => fetchLatestFirmwareCheckRun(firmwareId),
    enabled: Number.isFinite(firmwareId) && firmwareId > 0,
    refetchInterval: (current) => {
      const status = current.state.data?.run?.status;
      return status === "queued" || status === "running" ? 3000 : false;
    },
  });

  const loadCatalog = useMutation({
    mutationFn: () => startFirmwareCheckRun(firmwareId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmwareCheckRunKey(firmwareId) });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const runSequence = useMutation({
    mutationFn: (commands: string[]) => runFirmwareCommandSequence(firmwareId, commands),
    onSuccess: (response) => {
      setSequenceDocument(response.result);
      setSteps(sectionRows(response.result, "steps"));
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const document = checkQuery.data?.result ?? null;
  const commands = sectionRows(document, "commands");
  const configuration = sectionRows(sequenceDocument ?? document, "configuration");
  const graph = useMemo(() => buildGraph(sequenceDocument ?? document), [document, sequenceDocument]);
  const fileName = detailQuery.data?.firmware?.fileName ?? "Logic analyzer";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHint="Command list, the configuration those commands change, a sequence to run, and the firmware logic states."
          breadcrumbLabel="Logic analyzer"
        >
          {fileName}
        </PageTitleWithHelp>
        <Button variant="secondary" disabled={loadCatalog.isPending} onClick={() => loadCatalog.mutate()}>
          Load from firmware
        </Button>
      </header>
      <p className="text-sm text-slate-600">For SGS-owned firmware only.</p>
      {checkQuery.data?.run ? (
        <p className="text-sm text-slate-700">
          {checkQuery.data.run.status}
          {checkQuery.data.run.toolVersion ? ` · ${checkQuery.data.run.toolVersion}` : ""}
        </p>
      ) : (
        <p className="text-sm text-slate-600">Load from firmware to fill the command list, configuration, and logic map.</p>
      )}
      {checkQuery.data?.run?.error ? <p className="text-sm text-red-700">{checkQuery.data.run.error}</p> : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <HubDataTable
          fillViewport={false}
          title="Command list"
          columns={[
            { id: "protocol", header: "Protocol", cell: (row: Row) => text(row.protocol) },
            { id: "name", header: "Command", cell: (row: Row) => text(row.name) },
            { id: "note", header: "What it does", cell: (row: Row) => text(row.note) },
          ]}
          rows={commands}
          rowKey={(row) => `${text(row.protocol)}:${text(row.name)}`}
          empty="No commands loaded."
        />
        <HubDataTable
          fillViewport={false}
          title="Current configuration"
          columns={[
            { id: "name", header: "Setting", cell: (row: Row) => text(row.name) },
            { id: "value", header: "Value", cell: (row: Row) => text(row.value) },
            { id: "changed_by", header: "Changed by", cell: (row: Row) => text(row.changed_by) },
          ]}
          rows={configuration}
          rowKey={(row) => text(row.name)}
          empty="No configuration loaded."
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sequence</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor="logic-sequence">Commands, one per line</Label>
            <textarea
              id="logic-sequence"
              className="mt-1 min-h-28 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
              value={sequenceText}
              onChange={(event) => setSequenceText(event.target.value)}
              placeholder={"FAULT 1\nPFRH 4\nCELL 20"}
            />
          </div>
          <Button
            variant="primary"
            disabled={runSequence.isPending}
            onClick={() => {
              const commandsToRun = sequenceText
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter((line) => line !== "");
              runSequence.mutate(commandsToRun);
            }}
          >
            Run sequence
          </Button>
          {steps.length > 0 ? (
            <ol className="space-y-2 text-sm">
              {steps.map((step) => (
                <li key={text(step.step)}>
                  <span className="font-medium">{text(step.step)}. {text(step.command)}</span>
                  <div className="text-slate-700">{text(step.outcome)}</div>
                </li>
              ))}
            </ol>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logic map</CardTitle>
        </CardHeader>
        <CardBody>
          <LogicMap nodes={graph.nodes} edges={graph.edges} />
        </CardBody>
      </Card>
    </div>
  );
}

type GraphNode = { id: string; label: string; kind: string };
type GraphEdge = { from: string; to: string; when: string };

function buildGraph(document: FirmwareCheckDocument | null): { nodes: GraphNode[]; edges: GraphEdge[] } {
  return {
    nodes: sectionRows(document, "nodes")
      .filter((row) => text(row.kind) === "state" || text(row.kind) === "outcome")
      .map((row) => ({ id: text(row.id), label: text(row.label), kind: text(row.kind) })),
    edges: sectionRows(document, "edges").map((row) => ({
      from: text(row.from),
      to: text(row.to),
      when: text(row.when),
    })),
  };
}

function LogicMap(props: { nodes: GraphNode[]; edges: GraphEdge[] }): JSX.Element {
  if (props.nodes.length === 0) {
    return <p className="text-sm text-slate-600">Load from firmware to draw the fault and power states.</p>;
  }
  const width = 720;
  const height = Math.max(160, props.nodes.length * 28);
  const positions = new Map<string, { x: number; y: number }>();
  let stateIndex = 0;
  let outcomeIndex = 0;
  for (const node of props.nodes) {
    if (node.kind === "outcome") {
      positions.set(node.id, { x: 420, y: 24 + outcomeIndex * 36 });
      outcomeIndex += 1;
    } else {
      positions.set(node.id, { x: 16, y: 24 + stateIndex * 36 });
      stateIndex += 1;
    }
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Firmware logic states">
      {props.edges.map((edge) => {
        const from = positions.get(edge.from);
        const to = positions.get(edge.to);
        if (from == null || to == null) return null;
        return (
          <line
            key={`${edge.from}-${edge.to}`}
            x1={from.x + 220}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            stroke="#94a3b8"
          />
        );
      })}
      {props.nodes.map((node) => {
        const at = positions.get(node.id);
        if (at == null) return null;
        return (
          <g key={node.id}>
            <rect x={at.x} y={at.y - 12} width={220} height={22} rx={4} fill={node.kind === "outcome" ? "#fef3c7" : "#f8fafc"} stroke="#cbd5e1" />
            <text x={at.x + 8} y={at.y + 4} fontSize={11} fill="#0f172a">
              {node.label.slice(0, 32)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
